import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { api } from '../api/client.ts';
import type {
  AdminAdapter,
  AdminAiSettings,
  AdminAiUsage,
  AdminCustomer,
  AdminCustomerDetail,
  AdminOverview,
  PlatformSettings,
} from '../api/types.ts';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  ChoiceGroup,
  EmptyState,
  Field,
  Notice,
  Skeleton,
  TextInput,
  type Tone,
} from '../components/ui.tsx';
import { When } from '../components/operational.tsx';
import './Admin.css';

/**
 * The platform administration console.
 *
 * Reached only from a navigation entry that exists only for a session
 * carrying `platformAdmin`, and every request it makes is to `/api/admin/*`,
 * which refuses anybody else. The hiding is a courtesy for the operator's own
 * screen; the refusal that counts is the server's, and this screen would show
 * nothing but errors to an account that got here another way.
 *
 * What it shows is counts, status and metadata. There is no route from here to
 * a customer's panel password, private key or credentials, because running the
 * platform is not the same as being inside somebody's account — and an
 * operator who needs one of those should have to ask the customer.
 */

export type AdminSection =
  | 'dashboard'
  | 'customers'
  | 'users'
  | 'servers'
  | 'installations'
  | 'tunnels'
  | 'adapters'
  | 'backups'
  | 'incidents'
  | 'activity'
  | 'ai'
  | 'settings';

export const ADMIN_SECTIONS: readonly AdminSection[] = [
  'dashboard',
  'customers',
  'users',
  'servers',
  'installations',
  'tunnels',
  'adapters',
  'backups',
  'incidents',
  'activity',
  'ai',
  'settings',
];

export function Admin({
  section,
  customerId,
  onNavigate,
}: {
  section: AdminSection;
  customerId?: string;
  onNavigate: (section: AdminSection, customerId?: string) => void;
}): ReactNode {
  const { t } = useI18n();

  return (
    <div className="admin">
      <header className="page-header">
        <div>
          <h1>{t('admin.title')}</h1>
          <p className="muted">{t('admin.subtitle')}</p>
        </div>
      </header>

      <nav className="admin__tabs" aria-label={t('admin.title')}>
        {ADMIN_SECTIONS.map((name) => (
          <button
            key={name}
            type="button"
            className={['admin__tab', section === name && !customerId ? 'is-active' : '']
              .filter(Boolean)
              .join(' ')}
            aria-current={section === name && !customerId ? 'page' : undefined}
            onClick={() => onNavigate(name)}
          >
            {t(`admin.section.${name}` as never)}
          </button>
        ))}
      </nav>

      {customerId ? (
        <CustomerDetail id={customerId} onBack={() => onNavigate('customers')} />
      ) : section === 'dashboard' ? (
        <AdminDashboard />
      ) : section === 'customers' ? (
        <Customers onOpen={(id) => onNavigate('customers', id)} />
      ) : section === 'adapters' ? (
        <AdaptersSection />
      ) : section === 'ai' ? (
        <AiSettingsSection />
      ) : section === 'settings' ? (
        <SettingsSection />
      ) : (
        <ListSection section={section} />
      )}
    </div>
  );
}

// ---- dashboard -------------------------------------------------------------

function AdminDashboard(): ReactNode {
  const { t } = useI18n();
  const [overview, setOverview] = useState<AdminOverview>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    void api
      .adminOverview()
      .then(setOverview)
      .catch(() => setError(t('common.somethingWentWrong')));
  }, [t]);

  if (error) return <Notice tone="danger">{error}</Notice>;
  if (!overview) return <Skeleton lines={6} />;

  return (
    <div className="admin__stack">
      <div className="admin__metrics">
        <Metric label={t('admin.metric.customers')} value={overview.organizations} />
        <Metric label={t('admin.metric.activeCustomers')} value={overview.activeLastWeek} />
        <Metric
          label={t('admin.metric.suspended')}
          value={overview.suspendedOrganizations}
          tone={overview.suspendedOrganizations > 0 ? 'warning' : 'neutral'}
        />
        <Metric label={t('admin.metric.telegramCustomers')} value={overview.telegramCustomers} />
        <Metric label={t('admin.metric.users')} value={overview.activeUsers} />
        <Metric label={t('admin.metric.servers')} value={overview.servers} />
        <Metric label={t('admin.metric.installations')} value={overview.installations} />
        <Metric label={t('admin.metric.tunnels')} value={overview.tunnels} />
        <Metric
          label={t('admin.metric.unhealthyTunnels')}
          value={overview.unhealthyTunnels}
          tone={overview.unhealthyTunnels > 0 ? 'danger' : 'success'}
        />
        <Metric
          label={t('admin.metric.failedDeployments')}
          value={overview.failedDeployments}
          tone={overview.failedDeployments > 0 ? 'warning' : 'neutral'}
        />
        <Metric
          label={t('admin.metric.incidents')}
          value={overview.openIncidents}
          tone={overview.openIncidents > 0 ? 'danger' : 'success'}
        />
        <Metric
          label={t('admin.metric.backupFailures')}
          value={overview.backupFailuresLastWeek}
          tone={overview.backupFailuresLastWeek > 0 ? 'danger' : 'success'}
        />
        <Metric
          label={t('admin.metric.certificates')}
          value={overview.certificateProblems}
          tone={overview.certificateProblems > 0 ? 'warning' : 'success'}
        />
      </div>

      <Breakdown title={t('admin.metric.panelsByAdapter')} counts={overview.panelsByAdapter} />
      <Breakdown
        title={t('admin.metric.deploymentsByShape')}
        counts={overview.deploymentsByShape}
      />
      <Breakdown title={t('admin.metric.serversByStatus')} counts={overview.serversByStatus} />
    </div>
  );
}

function Metric({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  tone?: Tone;
}): ReactNode {
  return (
    <Card className="admin__metric">
      <p className="admin__metric-label">{label}</p>
      <p className="admin__metric-value">
        <Badge tone={tone}>{value}</Badge>
      </p>
    </Card>
  );
}

function Breakdown({
  title,
  counts,
}: {
  title: string;
  counts: Readonly<Record<string, number>>;
}): ReactNode {
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;
  return (
    <Card>
      <CardHeader title={title} />
      <ul className="key-values">
        {entries.map(([key, value]) => (
          <li key={key}>
            <span className="ltr-text">{key}</span>
            <strong>{value}</strong>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---- customers -------------------------------------------------------------

function Customers({ onOpen }: { onOpen: (id: string) => void }): ReactNode {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState<readonly AdminCustomer[]>();
  const [error, setError] = useState<string>();

  const load = useCallback(
    (term: string) => {
      void api
        .adminCustomers(term || undefined)
        .then((result) => setCustomers(result.customers))
        .catch(() => setError(t('common.somethingWentWrong')));
    },
    [t],
  );

  useEffect(() => load(''), [load]);

  if (error) return <Notice tone="danger">{error}</Notice>;

  return (
    <div className="admin__stack">
      <Card>
        <form
          className="admin__row"
          onSubmit={(event) => {
            event.preventDefault();
            load(search);
          }}
        >
          <Field label={t('admin.customers.search')}>
            {(props) => (
              <TextInput
                {...props}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            )}
          </Field>
          <Button type="submit" variant="secondary">
            {t('admin.customers.search')}
          </Button>
        </form>
      </Card>

      {!customers ? (
        <Skeleton lines={4} />
      ) : customers.length === 0 ? (
        <EmptyState title={t('admin.customers.none')} />
      ) : (
        <ul className="admin__cards">
          {customers.map((customer) => (
            <li key={customer.organizationId}>
              <Card>
                <CardHeader
                  title={customer.displayName ?? customer.name}
                  description={
                    <>
                      <StatusBadges customer={customer} />{' '}
                      <span className="ltr-text">{customer.organizationId}</span>
                    </>
                  }
                  actions={
                    <Button variant="secondary" onClick={() => onOpen(customer.organizationId)}>
                      {t('admin.customers.open')}
                    </Button>
                  }
                />
                <ul className="key-values">
                  <li>
                    <span>{t('admin.customers.telegramId')}</span>
                    <strong className="ltr-text">{customer.telegramUserId ?? '—'}</strong>
                  </li>
                  <li>
                    <span>{t('admin.customers.username')}</span>
                    <strong className="ltr-text">
                      {customer.telegramUsername ? `@${customer.telegramUsername}` : '—'}
                    </strong>
                  </li>
                  <li>
                    <span>{t('admin.customers.created')}</span>
                    <strong>
                      <When value={customer.createdAt} />
                    </strong>
                  </li>
                  <li>
                    <span>{t('admin.customers.lastSeen')}</span>
                    <strong>
                      {customer.lastSeenAt ? (
                        <When value={customer.lastSeenAt} />
                      ) : (
                        t('admin.status.never')
                      )}
                    </strong>
                  </li>
                  <li>
                    <span>{t('admin.customers.resources')}</span>
                    <strong>
                      {customer.servers} · {customer.installations} · {customer.tunnels}
                    </strong>
                  </li>
                </ul>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusBadges({ customer }: { customer: AdminCustomer }): ReactNode {
  const { t } = useI18n();
  return (
    <>
      <Badge tone={customer.status === 'suspended' ? 'danger' : 'success'}>
        {customer.status === 'suspended' ? t('admin.status.suspended') : t('admin.status.active')}
      </Badge>
      {customer.deploymentsDisabled ? (
        <Badge tone="warning">{t('admin.status.deploymentsPaused')}</Badge>
      ) : null}
    </>
  );
}

function CustomerDetail({ id, onBack }: { id: string; onBack: () => void }): ReactNode {
  const { t } = useI18n();
  const [detail, setDetail] = useState<AdminCustomerDetail>();
  const [error, setError] = useState<string>();
  const [done, setDone] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(() => {
    void api
      .adminCustomer(id)
      .then(setDetail)
      .catch(() => setError(t('common.somethingWentWrong')));
  }, [id, t]);

  useEffect(load, [load]);

  const act = (run: () => Promise<unknown>): void => {
    setDone(false);
    void run()
      .then(() => {
        setDone(true);
        setReason('');
        load();
      })
      .catch(() => setError(t('common.somethingWentWrong')));
  };

  if (error) return <Notice tone="danger">{error}</Notice>;
  if (!detail) return <Skeleton lines={6} />;
  const { customer } = detail;
  const enough = reason.trim().length >= 3;

  return (
    <div className="admin__stack">
      <Button variant="ghost" onClick={onBack}>
        {t('common.back')}
      </Button>

      <Card>
        <CardHeader
          title={customer.displayName ?? customer.name}
          description={<StatusBadges customer={customer} />}
        />
        {customer.statusReason ? <p className="muted">{customer.statusReason}</p> : null}
        <Notice tone="info">{t('admin.suspendNote')}</Notice>

        <Field label={t('admin.action.reason')} help={t('admin.action.reasonHelp')} required>
          {(props) => (
            <TextInput
              {...props}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </Field>

        <div className="admin__row">
          {customer.status === 'suspended' ? (
            <Button disabled={!enough} onClick={() => act(() => api.adminReactivate(id, reason))}>
              {t('admin.action.reactivate')}
            </Button>
          ) : (
            <Button
              variant="danger"
              disabled={!enough}
              onClick={() => act(() => api.adminSuspend(id, reason))}
            >
              {t('admin.action.suspend')}
            </Button>
          )}
          <Button
            variant="secondary"
            disabled={!enough}
            onClick={() =>
              act(() => api.adminSetDeployments(id, customer.deploymentsDisabled, reason))
            }
          >
            {customer.deploymentsDisabled
              ? t('admin.action.resumeDeployments')
              : t('admin.action.pauseDeployments')}
          </Button>
          <Button
            variant="secondary"
            disabled={!enough}
            onClick={() => act(() => api.adminRevokeSessions(id, reason))}
          >
            {t('admin.action.revokeSessions')}
          </Button>
          {/* Needs no reason: it reads and repairs, changes nothing an
              operator did not already approve, and pressing it twice is one
              check rather than two. */}
          <Button variant="secondary" onClick={() => act(() => api.adminRecheck(id))}>
            {t('admin.action.recheck')}
          </Button>
          <Button
            variant="secondary"
            disabled={!enough}
            onClick={() =>
              act(() =>
                api.adminSetMuted(id, detail.muted.length > 0 ? [] : ALL_NOTIFICATIONS, reason),
              )
            }
          >
            {detail.muted.length > 0 ? t('admin.action.unmute') : t('admin.action.mute')}
          </Button>
        </div>
        <p className="muted">{t('admin.action.recheckHelp')}</p>
        {done ? <Notice tone="success">{t('admin.action.done')}</Notice> : null}
      </Card>

      <Notice tone="info">{t('admin.detail.noSecrets')}</Notice>

      <Rows title={t('admin.detail.servers')} rows={detail.servers} />
      <Rows title={t('admin.detail.deployments')} rows={detail.deployments} />
      <Rows title={t('admin.detail.installations')} rows={detail.installations} />
      <Rows title={t('admin.detail.tunnels')} rows={detail.tunnels} />
      <Rows title={t('admin.detail.incidents')} rows={detail.incidents} />
      <Rows title={t('admin.detail.audit')} rows={detail.audit} />
    </div>
  );
}

/**
 * Everything this platform can tell a customer.
 *
 * Muting from here is all-or-nothing on purpose: an operator reaching for it
 * is stopping an alert storm, and choosing which nine events to silence is a
 * preferences screen rather than a decision to make during one.
 */
const ALL_NOTIFICATIONS = [
  'deployment.completed',
  'deployment.failed',
  'server.offline',
  'node.offline',
  'tunnel.down',
  'tunnel.degraded',
  'certificate.expiring',
  'backup.failed',
  'disk.high_usage',
];

// ---- the read-only lists ---------------------------------------------------

function ListSection({ section }: { section: AdminSection }): ReactNode {
  const { t } = useI18n();
  const [rows, setRows] = useState<readonly Record<string, unknown>[]>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    // Wrapped rather than calling `setRows` in the effect's own body: a
    // synchronous setState there is a cascading render. Everything after the
    // await already happens on a later tick.
    void (async () => {
      setRows(undefined);
      try {
        const result = await api.adminList(section as 'users');
        setRows(Object.values(result)[0] ?? []);
      } catch {
        setError(t('common.somethingWentWrong'));
      }
    })();
  }, [section, t]);

  if (error) return <Notice tone="danger">{error}</Notice>;
  if (!rows) return <Skeleton lines={5} />;
  return <Rows title={t(`admin.section.${section}` as never)} rows={rows} />;
}

/**
 * A table of whatever the server sent.
 *
 * Deliberately generic. These are operator views over rows whose columns the
 * API chooses, and a hand-written table per section would be ten components
 * that drift from the queries behind them. What matters is that nothing
 * secret is in the rows — that is enforced where the rows are selected, in
 * `db/platform.ts`, rather than by hoping a renderer leaves a column out.
 */
function Rows({
  title,
  rows,
}: {
  title: string;
  rows: readonly Record<string, unknown>[];
}): ReactNode {
  const columns = [...new Set(rows.flatMap((row) => Object.keys(row)))];
  return (
    <Card>
      <CardHeader title={`${title} · ${rows.length}`} />
      {rows.length === 0 ? (
        <p className="muted">—</p>
      ) : (
        <div className="admin__table-scroll">
          <table className="admin__table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column} className="ltr-text">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={String(row['id'] ?? index)}>
                  {columns.map((column) => (
                    <td key={column} className="ltr-text">
                      {renderCell(row[column])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function renderCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// ---- what this build can install -------------------------------------------

/**
 * The adapter catalog.
 *
 * Read from the registry on the server, so it cannot list something the
 * planner would refuse and cannot omit an adapter somebody added. It is split
 * in two because those are the only two states that matter to an operator:
 * what a customer can be given, and what exists but is deliberately withheld —
 * with the reason beside it, because "not yet" without a reason is the same as
 * silence.
 */
function AdaptersSection(): ReactNode {
  const { t } = useI18n();
  const [adapters, setAdapters] = useState<readonly AdminAdapter[]>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    void (async () => {
      try {
        setAdapters((await api.adminAdapters()).adapters);
      } catch {
        setError(t('common.somethingWentWrong'));
      }
    })();
  }, [t]);

  if (error) return <Notice tone="danger">{error}</Notice>;
  if (!adapters) return <Skeleton lines={6} />;

  const offered = adapters.filter((adapter) => adapter.offered);
  const withheld = adapters.filter((adapter) => !adapter.offered);

  return (
    <div className="admin__stack">
      <p className="muted">{t('admin.adapters.explain')}</p>
      <AdapterGroup title={t('admin.adapters.offered')} adapters={offered} />
      <AdapterGroup title={t('admin.adapters.withheld')} adapters={withheld} />
    </div>
  );
}

function AdapterGroup({
  title,
  adapters,
}: {
  title: string;
  adapters: readonly AdminAdapter[];
}): ReactNode {
  const { t, pick } = useI18n();
  return (
    <Card>
      <CardHeader title={`${title} · ${adapters.length}`} />
      {adapters.length === 0 ? (
        <p className="muted">{t('admin.adapters.none')}</p>
      ) : (
        <ul className="admin__adapters">
          {adapters.map((adapter) => (
            <li key={adapter.adapterId} className="admin__adapter">
              <div className="admin__adapter-head">
                <strong>{adapter.name}</strong>
                <Badge tone={adapter.offered ? 'success' : 'warning'}>{adapter.maturity}</Badge>
                <span className="muted ltr-text">{adapter.kind}</span>
              </div>
              <p>{pick(adapter.summary)}</p>
              {adapter.notOfferedBecause ? (
                <Notice tone="warning">{adapter.notOfferedBecause}</Notice>
              ) : null}
              <p className="muted ltr-text">
                {adapter.upstream.project} · {adapter.upstream.license} ·{' '}
                {adapter.upstream.testedVersion ?? adapter.upstream.channel} ·{' '}
                {t('admin.adapters.verified')} {adapter.upstream.verifiedAt}
              </p>
              {adapter.securityNotes.length > 0 ? (
                <p className="muted">
                  {t('admin.adapters.caveats')}:{' '}
                  {adapter.securityNotes.map((note) => note.title).join(' · ')}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---- settings --------------------------------------------------------------

/**
 * What the AI is doing, what it is costing, and the ceiling on that.
 *
 * Deliberately shows both providers. "The local model handled nine hundred of
 * a thousand questions" is the number that makes the hundred worth paying
 * for, and it is invisible if only the paid ones are counted.
 *
 * There is no field here for the API key. The key is configuration on the
 * server, write-only by design, and a screen that could show it would make
 * every operator session a copy of it.
 */
function AiSection({
  budget,
  onBudget,
}: {
  budget: { calls: number; tokens: number };
  onBudget: (patch: PlatformSettings) => void;
}): ReactNode {
  const { t } = useI18n();
  const [usage, setUsage] = useState<AdminAiUsage>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    void api
      .adminAi()
      .then(setUsage)
      .catch(() => setFailed(true));
  }, []);

  return (
    <Card>
      <CardHeader title={t('admin.ai.title')} description={t('admin.ai.help')} />
      {failed ? <Notice tone="danger">{t('common.somethingWentWrong')}</Notice> : null}
      {!failed && !usage ? <Skeleton lines={3} /> : null}
      {usage ? (
        <>
          <p className="muted">
            {!usage.configured ? (
              t('admin.ai.off')
            ) : (
              <Badge tone={usage.available ? 'success' : 'warning'}>
                {usage.available ? t('admin.ai.available') : t('admin.ai.unavailable')}
              </Badge>
            )}
          </p>
          {usage.configured && !usage.fallbackConfigured ? (
            <p className="muted">{t('admin.ai.noFallback')}</p>
          ) : null}

          <h4>{t('admin.ai.thisMonth')}</h4>
          {usage.providers.length === 0 ? (
            <p className="muted">{t('admin.ai.nothingYet')}</p>
          ) : (
            <ul className="admin__list">
              {usage.providers.map((provider) => (
                <li key={provider.provider}>
                  <span className="ltr-text">{provider.provider}</span>
                  {' — '}
                  {t('admin.ai.calls')}: {provider.calls}
                  {', '}
                  {t('admin.ai.escalations')}: {provider.escalations}
                  {', '}
                  {t('admin.ai.tokens')}: {provider.promptTokens + provider.completionTokens}
                  {', '}
                  {t('admin.ai.cost')}:{' '}
                  {provider.estimatedUsd === null
                    ? t('admin.ai.noPrice')
                    : `$${provider.estimatedUsd.toFixed(2)}`}
                </li>
              ))}
            </ul>
          )}

          <Field label={t('admin.ai.budgetCalls')} help={t('admin.ai.budgetHelp')}>
            {(props) => (
              <TextInput
                {...props}
                type="number"
                min={0}
                value={budget.calls || ''}
                onChange={(event) =>
                  onBudget({ aiFallbackMonthlyCalls: Number(event.target.value) || 0 })
                }
              />
            )}
          </Field>
          <Field label={t('admin.ai.budgetTokens')}>
            {(props) => (
              <TextInput
                {...props}
                type="number"
                min={0}
                value={budget.tokens || ''}
                onChange={(event) =>
                  onBudget({ aiFallbackMonthlyTokens: Number(event.target.value) || 0 })
                }
              />
            )}
          </Field>
        </>
      ) : null}
    </Card>
  );
}

function SettingsSection(): ReactNode {
  const { t } = useI18n();
  const [settings, setSettings] = useState<PlatformSettings>();
  const [panels, setPanels] = useState<readonly string[]>([]);
  const [tunnels, setTunnels] = useState<readonly string[]>([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    void api
      .adminSettings()
      .then((result) => {
        setSettings(result.settings);
        setPanels(result.availablePanelAdapters);
        setTunnels(result.availableTunnelAdapters);
      })
      .catch(() => setError(t('common.somethingWentWrong')));
  }, [t]);

  if (error) return <Notice tone="danger">{error}</Notice>;
  if (!settings) return <Skeleton lines={6} />;

  const update = (patch: PlatformSettings): void => {
    setSaved(false);
    setSettings({ ...settings, ...patch });
  };
  const toggleAdapter = (
    key: 'allowedPanelAdapters' | 'allowedTunnelAdapters',
    available: readonly string[],
    id: string,
    on: boolean,
  ): void => {
    // Absent means "no opinion", so the first tick has to start from
    // everything that exists rather than from an empty list — otherwise
    // unticking one adapter would silently switch off all the others.
    const current = settings[key] ?? available;
    update({ [key]: on ? [...current, id] : current.filter((each) => each !== id) });
  };

  return (
    <div className="admin__stack">
      <Card>
        <CardHeader title={t('admin.settings.title')} />
        <Checkbox
          label={t('admin.settings.telegramSignup')}
          help={t('admin.settings.telegramSignupHelp')}
          checked={settings.allowTelegramSignup !== false}
          onChange={(on) => update({ allowTelegramSignup: on })}
        />
        <Checkbox
          label={t('admin.settings.maintenance')}
          help={t('admin.settings.maintenanceHelp')}
          checked={settings.maintenanceMode === true}
          onChange={(on) => update({ maintenanceMode: on })}
        />
        <Field label={t('admin.settings.maintenanceMessage')}>
          {(props) => (
            <TextInput
              {...props}
              value={settings.maintenanceMessage ?? ''}
              onChange={(event) => update({ maintenanceMessage: event.target.value })}
            />
          )}
        </Field>
        <Field label={t('admin.settings.serverLimit')}>
          {(props) => (
            <TextInput
              {...props}
              type="number"
              min={0}
              value={settings.defaultServerLimit ?? ''}
              onChange={(event) =>
                update({ defaultServerLimit: Number(event.target.value) || undefined })
              }
            />
          )}
        </Field>
        <Field label={t('admin.settings.deploymentLimit')}>
          {(props) => (
            <TextInput
              {...props}
              type="number"
              min={0}
              value={settings.defaultDeploymentLimit ?? ''}
              onChange={(event) =>
                update({ defaultDeploymentLimit: Number(event.target.value) || undefined })
              }
            />
          )}
        </Field>

        <ChoiceGroup
          legend={t('admin.settings.autonomy')}
          help={t('admin.settings.autonomyHelp')}
          columns={3}
          value={settings.defaultAutonomy ?? 'auto'}
          onChange={(value) => update({ defaultAutonomy: value })}
          options={[
            {
              value: 'safe_auto',
              title: t('admin.settings.autonomySafe'),
              description: t('admin.settings.autonomySafeHelp'),
            },
            {
              value: 'auto',
              title: t('admin.settings.autonomyAuto'),
              description: t('admin.settings.autonomyAutoHelp'),
              recommended: true,
            },
            {
              value: 'expert',
              title: t('admin.settings.autonomyExpert'),
              description: t('admin.settings.autonomyExpertHelp'),
            },
          ]}
        />
      </Card>

      <Card>
        <CardHeader
          title={t('admin.settings.panels')}
          description={t('admin.settings.adaptersHelp')}
        />
        {panels.map((id) => (
          <Checkbox
            key={id}
            label={<span className="ltr-text">{id}</span>}
            checked={(settings.allowedPanelAdapters ?? panels).includes(id)}
            onChange={(on) => toggleAdapter('allowedPanelAdapters', panels, id, on)}
          />
        ))}
      </Card>

      <Card>
        <CardHeader title={t('admin.settings.tunnels')} />
        {tunnels.map((id) => (
          <Checkbox
            key={id}
            label={<span className="ltr-text">{id}</span>}
            checked={(settings.allowedTunnelAdapters ?? tunnels).includes(id)}
            onChange={(on) => toggleAdapter('allowedTunnelAdapters', tunnels, id, on)}
          />
        ))}
      </Card>

      <Card>
        <CardHeader title={t('admin.settings.notifications')} />
        <Checkbox
          label={t('admin.settings.notifyDeployment')}
          checked={settings.notifyOnDeploymentFinished !== false}
          onChange={(on) => update({ notifyOnDeploymentFinished: on })}
        />
        <Checkbox
          label={t('admin.settings.notifyIncident')}
          checked={settings.notifyOnIncident !== false}
          onChange={(on) => update({ notifyOnIncident: on })}
        />
        <Checkbox
          label={t('admin.settings.notifyBackup')}
          checked={settings.notifyOnBackupFailure !== false}
          onChange={(on) => update({ notifyOnBackupFailure: on })}
        />
      </Card>

      <div className="admin__row">
        <Button
          onClick={() => {
            void api
              .adminSaveSettings(settings)
              .then((result) => {
                setSettings(result.settings);
                setSaved(true);
              })
              .catch(() => setError(t('common.somethingWentWrong')));
          }}
        >
          {t('admin.settings.save')}
        </Button>
      </div>
      {saved ? <Notice tone="success">{t('admin.settings.saved')}</Notice> : null}
    </div>
  );
}

// ---- the AI ----------------------------------------------------------------

/**
 * Where the AI is, and what it may cost.
 *
 * The screen an operator needs in order to point this platform at their own
 * inference server, or to turn the paid fallback on, without editing a unit
 * file on a machine they may not be sitting at. Everything here takes effect
 * as soon as it is saved.
 *
 * Neither key is ever displayed. The form says whether one is stored and when
 * it last changed; an empty box means "leave it alone", which is the only
 * thing it can mean for a value nobody is allowed to read back.
 */
function AiSettingsSection(): ReactNode {
  const { t } = useI18n();
  const [loaded, setLoaded] = useState<AdminAiSettings>();
  const [draft, setDraft] = useState<AdminAiSettings['settings']>({});
  /*
   * The monthly ceiling lives in the platform settings, because it is read on
   * every call by the budget the advisor consults. It is edited here, on the
   * screen it belongs to, and saved with everything else — one number, one
   * home, one Save.
   */
  const [platform, setPlatform] = useState<PlatformSettings>();
  const [localKey, setLocalKey] = useState<string>();
  const [claudeKey, setClaudeKey] = useState<string>();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string>();
  const [testing, setTesting] = useState<'local' | 'claude'>();
  const [tested, setTested] = useState<Record<string, { available: boolean; detail?: string }>>({});

  useEffect(() => {
    void Promise.all([api.adminAiSettings(), api.adminSettings()])
      .then(([ai, settings]) => {
        setLoaded(ai);
        setDraft(ai.settings);
        setPlatform(settings.settings);
      })
      .catch(() => setError(t('common.somethingWentWrong')));
  }, [t]);

  if (error && !loaded) return <Notice tone="danger">{error}</Notice>;
  if (!loaded || !platform) return <Skeleton lines={8} />;

  const local = draft.local ?? {};
  const claude = draft.claude ?? {};
  const patchLocal = (patch: Partial<NonNullable<AdminAiSettings['settings']['local']>>): void => {
    setSaved(false);
    setDraft({ ...draft, local: { ...local, ...patch } });
  };
  const patchClaude = (
    patch: Partial<NonNullable<AdminAiSettings['settings']['claude']>>,
  ): void => {
    setSaved(false);
    setDraft({ ...draft, claude: { ...claude, ...patch } });
  };

  const test = (provider: 'local' | 'claude'): void => {
    setTesting(provider);
    void api
      .adminTestAi(provider)
      .then((result) => setTested((current) => ({ ...current, [provider]: result })))
      .catch(() => setTested((current) => ({ ...current, [provider]: { available: false } })))
      .finally(() => setTesting(undefined));
  };

  const keyState = (name: 'local' | 'claude'): ReactNode => (
    <>
      {loaded.credentials[name].configured ? t('admin.ai.apiKeySet') : t('admin.ai.apiKeyUnset')}{' '}
      {t('admin.ai.apiKeyNeverShown')}
    </>
  );

  const testResult = (provider: 'local' | 'claude'): ReactNode => {
    const result = tested[provider];
    if (!result) return null;
    return (
      <Notice tone={result.available ? 'success' : 'danger'}>
        {result.available
          ? t('admin.ai.testOk')
          : `${t('admin.ai.testFailed')}: ${result.detail ?? ''}`}
      </Notice>
    );
  };

  const save = (): void => {
    setError(undefined);
    void api.adminSaveSettings(platform).catch(() => setError(t('common.somethingWentWrong')));
    void api
      .adminSaveAiSettings({
        settings: draft,
        ...(localKey === undefined ? {} : { localApiKey: localKey }),
        ...(claudeKey === undefined ? {} : { claudeApiKey: claudeKey }),
      })
      .then((result) => {
        setLoaded({ ...loaded, ...result });
        setDraft(result.settings);
        setLocalKey(undefined);
        setClaudeKey(undefined);
        setSaved(true);
      })
      .catch((failure: { code?: string }) => {
        setError(
          failure?.code === 'invalid_ai_endpoint'
            ? t('admin.ai.badEndpoint')
            : t('common.somethingWentWrong'),
        );
      });
  };

  return (
    <div className="admin__stack">
      <Card>
        <CardHeader title={t('admin.ai.localTitle')} description={t('admin.ai.localHelp')} />
        <Checkbox
          label={t('admin.ai.localEnabled')}
          checked={local.enabled ?? loaded.effective.localEnabled}
          onChange={(on) => patchLocal({ enabled: on })}
        />
        <Field label={t('admin.ai.apiUrl')} help={t('admin.ai.apiUrlHelp')}>
          {(props) => (
            <TextInput
              {...props}
              className="ltr-text"
              placeholder={loaded.effective.apiUrl ?? 'https://'}
              value={local.apiUrl ?? ''}
              onChange={(event) => patchLocal({ apiUrl: event.target.value })}
            />
          )}
        </Field>
        <Field label={t('admin.ai.apiKey')} help={keyState('local')}>
          {(props) => (
            <TextInput
              {...props}
              type="password"
              autoComplete="off"
              className="ltr-text"
              value={localKey ?? ''}
              onChange={(event) => setLocalKey(event.target.value)}
            />
          )}
        </Field>
        <Checkbox
          label={t('admin.ai.apiKeyClear')}
          checked={localKey === ''}
          onChange={(on) => setLocalKey(on ? '' : undefined)}
        />
        <Field label={t('admin.ai.defaultModel')}>
          {(props) => (
            <TextInput
              {...props}
              className="ltr-text"
              placeholder={loaded.effective.defaultModel}
              value={local.defaultModel ?? ''}
              onChange={(event) => patchLocal({ defaultModel: event.target.value })}
            />
          )}
        </Field>

        <h4>{t('admin.ai.taskModels')}</h4>
        <p className="muted">{t('admin.ai.taskModelsHelp')}</p>
        {loaded.tasks.map((task) => (
          <Field key={task} label={<span className="ltr-text">{task}</span>}>
            {(props) => (
              <TextInput
                {...props}
                className="ltr-text"
                placeholder={loaded.effective.taskModels[task] ?? loaded.effective.defaultModel}
                value={local.taskModels?.[task] ?? ''}
                onChange={(event) =>
                  patchLocal({
                    taskModels: { ...(local.taskModels ?? {}), [task]: event.target.value },
                  })
                }
              />
            )}
          </Field>
        ))}

        <div className="admin__row">
          <Button variant="secondary" onClick={() => test('local')} disabled={testing === 'local'}>
            {testing === 'local' ? t('admin.ai.testing') : t('admin.ai.test')}
          </Button>
        </div>
        {testResult('local')}
      </Card>

      <Card>
        <CardHeader title={t('admin.ai.claudeTitle')} description={t('admin.ai.claudeHelp')} />
        <Checkbox
          label={t('admin.ai.claudeEnabled')}
          checked={claude.enabled ?? loaded.effective.claudeEnabled}
          onChange={(on) => patchClaude({ enabled: on })}
        />
        <Field label={t('admin.ai.apiKey')} help={keyState('claude')}>
          {(props) => (
            <TextInput
              {...props}
              type="password"
              autoComplete="off"
              className="ltr-text"
              value={claudeKey ?? ''}
              onChange={(event) => setClaudeKey(event.target.value)}
            />
          )}
        </Field>
        <Checkbox
          label={t('admin.ai.apiKeyClear')}
          checked={claudeKey === ''}
          onChange={(on) => setClaudeKey(on ? '' : undefined)}
        />
        <Field label={t('admin.ai.claudeModel')}>
          {(props) => (
            <TextInput
              {...props}
              className="ltr-text"
              placeholder={loaded.effective.claudeModel ?? ''}
              value={claude.model ?? ''}
              onChange={(event) => patchClaude({ model: event.target.value })}
            />
          )}
        </Field>
        <Field label={t('admin.ai.claudeEmergencyModel')} help={t('admin.ai.claudeEmergencyHelp')}>
          {(props) => (
            <TextInput
              {...props}
              className="ltr-text"
              placeholder={loaded.effective.claudeEmergencyModel ?? ''}
              value={claude.emergencyModel ?? ''}
              onChange={(event) => patchClaude({ emergencyModel: event.target.value })}
            />
          )}
        </Field>
        <Field label={t('admin.ai.maxOutputTokens')} help={t('admin.ai.maxOutputTokensHelp')}>
          {(props) => (
            <TextInput
              {...props}
              type="number"
              min={256}
              placeholder={String(loaded.effective.claudeMaxOutputTokens ?? '')}
              value={claude.maxOutputTokens ?? ''}
              onChange={(event) =>
                patchClaude({ maxOutputTokens: Number(event.target.value) || undefined })
              }
            />
          )}
        </Field>
        <div className="admin__row">
          <Button
            variant="secondary"
            onClick={() => test('claude')}
            disabled={testing === 'claude'}
          >
            {testing === 'claude' ? t('admin.ai.testing') : t('admin.ai.test')}
          </Button>
        </div>
        {testResult('claude')}
      </Card>

      <AiSection
        budget={{
          calls: platform.aiFallbackMonthlyCalls ?? 0,
          tokens: platform.aiFallbackMonthlyTokens ?? 0,
        }}
        onBudget={(patch) => {
          setSaved(false);
          setPlatform({ ...platform, ...patch });
        }}
      />

      <div className="admin__row">
        <Button onClick={save}>{t('admin.ai.save')}</Button>
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {saved ? <Notice tone="success">{t('admin.ai.saved')}</Notice> : null}
    </div>
  );
}
